import React, { useState, useEffect } from 'react';
import { goToEventDetail } from '../utils/navigation';
import { apiService } from '../services/api';
import AdSense from './AdSense';

interface CityEvent {
  id: number;
  title: string;
  location: string;
  startDate: string;
  endDate: string;
}

interface CityData {
  name: string;
  title: string;
  image: string;
  celebrationsCount: string;
  description: string;
  celebrations: string[];
  events: CityEvent[];
  loading: boolean;
}

const CityCelebrations: React.FC = () => {
  const [citiesData, setCitiesData] = useState<CityData[]>([]);

  const baseCities = [
    {
      name: 'Kolkata',
      title: 'Durga Puja in Kolkata',
      image: '/citywise/kolkata.jpeg.jpg',
      celebrationsCount: 'Thousands',
      description: 'UNESCO heritage, thousands of pujas',
      celebrations: [
        'Kumartuli Artisans',
        'College Square Puja',
        'Bagbazar Sarbojanin',
        'Bhowanipore 75 Pally',
        'Santosh Mitra Square'
      ]
    },
    {
      name: 'Asansol–Durgapur',
      title: 'Durga Puja in Asansol–Durgapur',
      image: '/citywise/asansol.jpeg.jpg',
      celebrationsCount: '100+',
      description: '100+ pujas, major hub in WB',
      celebrations: [
        'Asansol Bengali Association',
        'Durgapur Steel Plant Puja',
        'Burnpur Cultural Society',
        'Kulti Sarbojanin',
        'Raniganj Puja Committee'
      ]
    },
    {
      name: 'Siliguri',
      title: 'Durga Puja in Siliguri',
      image: '/citywise/siliguri-iskcon-temple.jpg',
      celebrationsCount: '82',
      description: 'Cultural vibrancy with 82 registered pandals',
      celebrations: [
        'Siliguri Bengali Association',
        'Hill Cart Road Puja',
        'Sevoke Road Sarbojanin',
        'Pradhan Nagar Cultural',
        'Bidhan Nagar Puja'
      ]
    },
    {
      name: 'Basirhat',
      title: 'Durga Puja in Basirhat',
      image: '/citywise/basirhat.jpeg.jpg',
      celebrationsCount: '50+',
      description: 'Boat immersions, vibrant thematic displays',
      celebrations: [
        'Basirhat Town Puja',
        'Ichhapur Cultural Society',
        'Taki River Puja',
        'Hasnabad Sarbojanin',
        'Minakhan Puja Committee'
      ]
    },
    {
      name: 'Cuttack',
      title: 'Durga Puja in Cuttack',
      image: '/citywise/cuttack.jpeg.jpg',
      celebrationsCount: '200+',
      description: 'Opulent idol adornment traditions (Suna Medha)',
      celebrations: [
        'Choudhury Bazar Puja',
        'Buxi Bazar Sarbojanin',
        'Cantonment Road Puja',
        'Tulasipur Cultural',
        'Mangalabag Puja Committee'
      ]
    },
    {
      name: 'Other West Bengal',
      title: 'Durga Puja in West Bengal',
      image: '/citywise/west bengal.jpeg.jpg',
      celebrationsCount: '500+',
      description: 'Rich traditions across Jalpaiguri, Birbhum, Bankura',
      celebrations: [
        'Jalpaiguri Cultural Society',
        'Birbhum Puja Samiti',
        'Bankura Town Puja',
        'Purulia Sarbojanin',
        'Malda Cultural Association'
      ]
    },
    {
      name: 'New Delhi (CR Park)',
      title: 'Durga Puja in New Delhi',
      image: '/citywise/new delhi.jpeg.jpg',
      celebrationsCount: '50+',
      description: 'Mini-Kolkata, authentic celebrations with cultural depth',
      celebrations: [
        'CR Park Kali Mandir',
        'Bengali Cultural Association',
        'Chittaranjan Park Block A',
        'Delhi Durga Puja Samiti',
        'Kashmere Gate Puja Committee'
      ]
    },
    {
      name: 'Mumbai',
      title: 'Durga Puja in Mumbai',
      image: '/citywise/mumbai.jpeg.jpg',
      celebrationsCount: '100+',
      description: 'Glamorous, high-footfall, celebrity attractions',
      celebrations: [
        'Lokhandwala Durga Puja',
        'Andheri Bengali Association',
        'Powai Durga Puja Samiti',
        'Shivaji Park Sarbojanin',
        'Bandra Bengali Cultural'
      ]
    }
  ];

  // Fetch events for each city
  useEffect(() => {
    const fetchCityEvents = async () => {
      
      const citiesWithEvents = await Promise.all(
        baseCities.map(async (city) => {
          try {
            console.log(`🔍 Fetching events for city: ${city.name}`);
            
            // Fetch events for this city
            const response = await apiService.getPublicEvents({
              limit: 5,
              search: city.name
            });

            console.log(`📡 API Response for ${city.name}:`, response);
            console.log(`📊 Response data:`, response);
            
            // Cast to any to handle dynamic response structure
            const responseData = response as any;
            console.log(`🎉 Events array:`, responseData?.events || responseData?.data?.events);

            // Try both possible response structures
            const events: CityEvent[] = responseData?.events || responseData?.data?.events || [];
            console.log(`📋 Parsed events for ${city.name}:`, events);
            
            // Get pagination data for total count
            const pagination = responseData?.pagination || responseData?.data?.pagination;
            const totalItems = pagination?.totalItems || pagination?.total || events.length;
            console.log(`📊 Total items for ${city.name}:`, totalItems);
            
            // Update celebrations list with real event names
            const eventNames = events.map(event => event.title);
            console.log(`🏷️ Event names for ${city.name}:`, eventNames);
            
            const celebrations = eventNames.length > 0 ? eventNames : [];
            console.log(`🎊 Final celebrations for ${city.name}:`, celebrations);
            
            // Update celebrations count using pagination total
            const celebrationsCount = totalItems > 0 ? `${totalItems}+` : '0';
            console.log(`🔢 Celebrations count for ${city.name}:`, celebrationsCount);

            const result = {
              ...city,
              events,
              celebrations,
              celebrationsCount,
              loading: false
            };
            
            console.log(`✅ Final result for ${city.name}:`, result);
            return result;
          } catch (error) {
            console.error(`❌ Failed to fetch events for ${city.name}:`, error);
            return {
              ...city,
              events: [],
              celebrations: city.celebrations, // Keep fallback celebrations on API error
              celebrationsCount: city.celebrationsCount, // Keep fallback count on API error
              loading: false
            };
          }
        })
      );

      setCitiesData(citiesWithEvents);
    };

    fetchCityEvents();
  }, []);

  const handleViewAll = (cityName: string) => {
    console.log(`🔍 Navigating to explore page with city filter: ${cityName}`);
    // Navigate to explore page with city filter as URL parameter
    const exploreUrl = `/all-events?city=${encodeURIComponent(cityName)}`;
    window.history.pushState({}, '', exploreUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleEventClick = (event: CityEvent) => {
    console.log(`🎯 Clicking on event:`, event);
    goToEventDetail(event.id, event.title, event.location);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-[#CC2861]" style={{ fontFamily: 'serif' }}>
            Durga Puja Celebrations by City
          </h2>
          <p className="text-xl text-gray-600">
            Discover local celebrations in your city and beyond
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {citiesData.map((city, index) => {
            console.log(`🎨 Rendering city ${index}:`, city.name, {
              loading: city.loading,
              celebrations: city.celebrations,
              celebrationsCount: city.celebrationsCount,
              events: city.events
            });
            return (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group border border-gray-100 flex flex-col h-full">
              <div className="relative">
                <img 
                  src={city.image} 
                  alt={`Durga Puja in ${city.name}`} 
                  className="w-full h-72 object-contain"
                />
                <div className="absolute top-3 right-3 bg-gradient-to-r from-dpn-pink to-dpn-raspberry text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg backdrop-blur-sm">
                  {city.celebrationsCount} celebrations
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-runtime text-xl font-bold text-gray-900 mb-2 group-hover:text-dpn-pink transition-colors line-clamp-2">
                  {city.title}
                </h3>
                
                <div className="flex items-center text-gray-600 mb-3">
                  <span className="font-lucy-rose text-sm">{city.description}</span>
                </div>

                <div className="flex-1 mb-4">
                  <ul className="space-y-2">
                    {(() => {
                      console.log(`🎭 Rendering events for ${city.name}:`, {
                        loading: city.loading,
                        eventsLength: city.events.length,
                        events: city.events,
                        celebrationsLength: city.celebrations.length,
                        celebrations: city.celebrations
                      });
                      
                      if (city.loading) {
                        console.log(`⏳ Showing loading state for ${city.name}`);
                        return Array.from({ length: 3 }).map((_, celebrationIndex) => (
                          <li 
                            key={celebrationIndex}
                            className="flex items-center gap-3"
                          >
                            <div className="w-2 h-2 bg-gray-300 rounded-full flex-shrink-0 animate-pulse"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
                          </li>
                        ));
                      } else if (city.events.length > 0) {
                        console.log(`✅ Showing ${city.events.length} events for ${city.name}`);
                        return city.events.slice(0, 3).map((event) => (
                          <li 
                            key={event.id}
                            className="flex items-center gap-3 text-gray-700 hover:text-dpn-pink transition-all duration-300 cursor-pointer transform hover:translate-x-1"
                            onClick={() => handleEventClick(event)}
                          >
                            <div className="w-2 h-2 bg-dpn-light-green rounded-full flex-shrink-0 transition-colors duration-300"></div>
                            <span className="text-sm font-medium transition-colors duration-300 line-clamp-1">{event.title}</span>
                          </li>
                        ));
                      } else {
                        console.log(`❌ Showing empty state for ${city.name}`);
                        return (
                          <li className="flex items-center gap-3 text-gray-500">
                            <div className="w-2 h-2 bg-gray-300 rounded-full flex-shrink-0"></div>
                            <span className="text-sm font-medium italic">No events configured yet</span>
                          </li>
                        );
                      }
                    })()}
                  </ul>
                </div>
                
                <div className="flex items-center justify-center mt-auto">
                  <button 
                    onClick={() => handleViewAll(city.name)}
                    className="w-full bg-gradient-to-r from-dpn-pink to-dpn-raspberry hover:from-dpn-raspberry hover:to-dpn-pink text-white py-2.5 px-4 rounded-lg transition-all duration-200 text-sm font-lucy-rose font-medium transform hover:scale-105 shadow-md"
                  >
                    View All
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* Advertisement Section */}
        <div className="mt-16 flex justify-center">
          <AdSense 
            adSlot="1234567894" 
            adFormat="rectangle"
            className="w-full max-w-md"
            adStyle={{ display: 'block', width: '300px', height: '250px' }}
          />
        </div>
      </div>
    </section>
  );
};

export default CityCelebrations;
